import React, { useMemo } from "react";
import {
  type Config,
  type Cta,
  getDefaultIcon,
  TokenHistoryLabel
} from "kadow-jetpack-model";
import { type FormikErrors } from "formik";
import KC_Card from "../../../../../designsystem/KC_Card/KC_Card";
import CTALinkFields from "./CTALinkFields";
import {
  KC_Text_Sizes,
  KC_Weights
} from "../../../../../designsystem/ds_commons";
import {
  faBars,
  faCircleCheck,
  faCircleXmark
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import KC_IconContainer from "../../../../../designsystem/KC_IconContainer/KC_IconContainer";
import KC_Toggle from "../../../../../designsystem/KC_FormInput/KC_Toggle";
import KC_Text from "../../../../../designsystem/typography/KC_Text/KC_Text";
import { tt, LANGUAGE } from "../../../../../utils/i18n/i18n";
import PlacesAutoComplete from "../../../../../components/GooglePlaceSearch/PlacesAutoComplete";
import SocialCtaField from "./SocialCtaField";
import {
  BASE_URL,
  KADOW_COLOR_NEGATIVE,
  KADOW_COLOR_POSITIVE
} from "../../../../../utils/constants";
import KC_UIText from "../../../../../designsystem/typography/KC_UIText/KC_UIText";
import "./styles.scss";
import { useAccountToDisplaySelector } from "../../../../../redux/actions/user";
import getDefaultTitleForCTA from "../../../../../utils/helpers/getDefaultTitleForCTA";
import { KC_PaddingEnum } from "../../../../../designsystem/KC_Card/component/model/KC_PaddingEnum";
import { useSubscriptionPlansSelector } from "../../../../../redux/actions/app";

interface CTACardProps {
  cta: Cta;
  idx: number;
  onToggle: () => void;
  setFieldValue: (field: string, value: any) => void;
  config: Config;
  isDragging?: boolean;
  errors: FormikErrors<Config>;
  canRemove?: boolean;
}

const CTASelectedCard: React.FC<CTACardProps> = (props) => {
  const { accountToDisplay } = useAccountToDisplaySelector();

  const ctaHasLink = useMemo(() => {
    return (
      props.cta.id === TokenHistoryLabel.Custom ||
      props.cta.id === TokenHistoryLabel.TripAdvisor ||
      props.cta.id === TokenHistoryLabel.WhatsappGroup ||
      props.cta.id === TokenHistoryLabel.DownloadApp
    );
  }, [props.cta.id]);

  const ctaIsSocial = useMemo(() => {
    return (
      props.cta.id === TokenHistoryLabel.Facebook ||
      props.cta.id === TokenHistoryLabel.SnapChat ||
      props.cta.id === TokenHistoryLabel.Instagram ||
      props.cta.id === TokenHistoryLabel.IGStory ||
      props.cta.id === TokenHistoryLabel.TikTok
    );
  }, [props.cta.id]);

  const ctaHasNoFields = useMemo(() => {
    return (
      props.cta.id === TokenHistoryLabel.Phone ||
      props.cta.id === TokenHistoryLabel.PostalCode ||
      props.cta.id === TokenHistoryLabel.Birthday ||
      props.cta.id === TokenHistoryLabel.FirstName ||
      props.cta.id === TokenHistoryLabel.NPS
    );
  }, [props.cta.id]);

  const ctaIsCustomForm = useMemo(() => {
    return props.cta.id === TokenHistoryLabel.CustomForm;
  }, [props.cta.id]);

  const ctaTagValue = useMemo(() => {
    switch (props.cta.id) {
      case TokenHistoryLabel.Google:
        return props.config.socials?.googlePlaceId;
      case TokenHistoryLabel.Instagram:
        return props.config.socials?.instagram;
      case TokenHistoryLabel.TikTok:
        return props.config.socials?.tiktok;
      case TokenHistoryLabel.Facebook:
        return props.config.socials?.facebook;
      case TokenHistoryLabel.SnapChat:
        return props.config.socials?.snapchat;
      case TokenHistoryLabel.DownloadApp:
        return props.config.socials?.appStoreId;
      default:
        return "";
    }
  }, [props.cta]);

  const selectedCTA =
    props.config.ctas?.find((c: Cta) => c.id === props.cta.id) || null;

  const onFilterPlaceSelected = async (
    value: any,
    setFieldValue: (key: string, value: any) => void
  ) => {
    if (value) {
      setFieldValue("socials.googlePlaceId", value.place_id);
      setFieldValue("socials.googleAddress", value.description);
    }
  };

  const getOrdinalSuffix = (num: number): string => {
    if (LANGUAGE === "en") {
      const lastTwoDigits = num % 100;
      const lastDigit = num % 10;

      if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return tt("ordinal_suffix_th");
      }

      switch (lastDigit) {
        case 1:
          return tt("ordinal_suffix_st");
        case 2:
          return tt("ordinal_suffix_nd");
        case 3:
          return tt("ordinal_suffix_rd");
        default:
          return tt("ordinal_suffix_th");
      }
    } else {
      // French ordinals
      return num === 1 ? tt("ordinal_suffix_ere") : tt("ordinal_suffix_eme");
    }
  };

  const ctaOrderText = useMemo(() => {
    const num = props.idx + 1;
    const suffix = getOrdinalSuffix(num);

    return (
      <>
        {num}
        <sup>{suffix}</sup> {tt("participation")}
      </>
    );
  }, [props.idx]);

  const allPlans = useSubscriptionPlansSelector();

  const plan = useMemo(() => {
    if (!accountToDisplay?.saasPlanId) {
      return null;
    }
    return allPlans?.find((p) => p._id === accountToDisplay.saasPlanId) ?? null;
  }, [allPlans, accountToDisplay]);

  const isGratis = useMemo(() => {
    return !plan?.price;
  }, [plan]);

  const DRAG_ELEMENT = useMemo(() => {
    return (
      <>
        {!!props.canRemove && <FontAwesomeIcon icon={faBars} />}
        <KC_Text
          size={KC_Text_Sizes.md}
          weight={KC_Weights.bold}
          text={ctaOrderText}
          style={{
            width: 130
          }}
        />
      </>
    );
  }, [props.canRemove, ctaOrderText]);

  return (
    <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center position-relative w-100 st-gap p-4 p-sm-0 cta-card-item-mobile-wrapper">
      {!isGratis && (
        <div className="d-flex st-gap align-items-center">{DRAG_ELEMENT}</div>
      )}

      <KC_Card
        padding={KC_PaddingEnum.cta}
        isCTACard={true}
        className={`border cta-card-item ${
          props.isDragging ? "is-dragging" : ""
        }`}
        extraStyle={{
          minWidth: "unset !important"
        }}
      >
        <div
          key={`cta-${props.cta}-${props.idx}`}
          className="w-100 d-flex align-items-center flex-column flex-md-row st-gap"
        >
          <div className="d-flex d-sm-none flex-row align-items-center justify-content-between w-100 gap-sm">
            <div className="d-flex flex-row align-items-center flex-grow-1 w-100">
              <KC_IconContainer
                imgSrc={`${BASE_URL}/img/logo/${getDefaultIcon(props.cta.id)}`}
              />
              <KC_UIText
                weight="bold"
                text={getDefaultTitleForCTA(props.cta.id)}
              />
            </div>
            <KC_Toggle
              id={`${props.cta.id}-toggle`}
              isChecked={true}
              onToggle={props.onToggle}
            />
          </div>

          <div className="d-none d-sm-block">
            <KC_IconContainer
              imgSrc={`${BASE_URL}/img/logo/${getDefaultIcon(props.cta.id)}`}
            />
          </div>

          <div className="flex-grow-1 d-flex flex-column w-100 gap-sm">
            <KC_UIText
              weight="bold"
              text={getDefaultTitleForCTA(props.cta.id)}
              className="d-none d-sm-block"
            />
            <div>
              {ctaIsCustomForm && (
                <KC_UIText
                  weight="reg"
                  text={tt("cta_custom_form_contact_team")}
                  className="text-muted"
                />
              )}
              {ctaHasNoFields && <div className="d-none" />}
              {props.cta.id === TokenHistoryLabel.Google && (
                <div className="d-flex flex-column gap-sm">
                  <div className="d-flex kc-input-wrapper flex-grow-1">
                    <PlacesAutoComplete
                      placeholder={tt(
                        "jp_onboarding_placeholder_google_place_id"
                      )}
                      onChange={async (e) => {
                        await onFilterPlaceSelected(e, props.setFieldValue);
                      }}
                      value={props.config.socials.googleAddress}
                      className="kc-input-wrapper"
                      required={true}
                    />
                    <div className="input-group-append p-2">
                      {props.config.socials?.googlePlaceId ? (
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          color={KADOW_COLOR_POSITIVE}
                          size={"lg"}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faCircleXmark}
                          color={KADOW_COLOR_NEGATIVE}
                          size={"lg"}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {props.cta.id === TokenHistoryLabel.Custom && (
              <div className="kc-input-wrapper">
                <input
                  type="text"
                  className="form-control kc_input"
                  placeholder={tt("cta_custom_button_text_placeholder")}
                  value={props.config?.ctas?.[props.idx]?.customBtnTxt}
                  onChange={(e) =>
                    props.setFieldValue(
                      `ctas[${props.idx}].customBtnTxt`,
                      e.target.value
                    )
                  }
                />
              </div>
            )}
            {ctaHasLink && selectedCTA != null && (
              <CTALinkFields
                idx={props.idx}
                cta={selectedCTA}
                setFieldValue={props.setFieldValue}
                errors={props.errors}
                config={props.config}
              />
            )}
            {ctaIsSocial && (
              <SocialCtaField
                cta={props.cta}
                idx={props.idx}
                ctaTagValue={ctaTagValue}
                setFieldValue={props.setFieldValue}
                config={props.config}
                errors={props.errors}
              />
            )}
          </div>
          <div className="flex-grow-1"></div>
          {!!props.canRemove && (
            <div className="d-none d-sm-block">
              <KC_Toggle
                id={`${props.cta.id}-toggle`}
                isChecked={true}
                onToggle={props.onToggle}
              />
            </div>
          )}
        </div>
      </KC_Card>
    </div>
  );
};

export default CTASelectedCard;
